import { DEFAULT_EXPORT_HEADER_NAME_MODE, PINNED_ATTRIBUTE_KEY } from './const'
import { type ExportHeaderNameMode } from './exportListPersonsToCsv'
import { type StoredExportConfig } from './types'

const EXPORT_CONFIG_STORAGE_KEY_PREFIX = 'list_csv_export_config_v1'

/**
 * Builds a per-entity storage key so the saved export configuration of one
 * contact entity (e.g. persons) never overwrites another (e.g. companies).
 * The schema name (e.g. "person", "company") is used as a stable suffix.
 */
const getExportConfigStorageKey = (schemaName: string): string =>
  `${EXPORT_CONFIG_STORAGE_KEY_PREFIX}_${schemaName}`

const isExportHeaderNameMode = (
  value: unknown,
): value is ExportHeaderNameMode => value === 'readable' || value === 'schema'

export const readStoredExportConfig = (
  schemaName: string,
): StoredExportConfig | null => {
  if (typeof localStorage === 'undefined') return null

  try {
    const serializedConfig = localStorage.getItem(
      getExportConfigStorageKey(schemaName),
    )
    if (!serializedConfig) return null

    const parsedConfig = JSON.parse(
      serializedConfig,
    ) as Partial<StoredExportConfig>
    if (!Array.isArray(parsedConfig.attributeKeys)) return null

    return {
      attributeKeys: parsedConfig.attributeKeys.filter(
        (attributeKey): attributeKey is string =>
          typeof attributeKey === 'string',
      ),
      headerNameMode: isExportHeaderNameMode(parsedConfig.headerNameMode)
        ? parsedConfig.headerNameMode
        : DEFAULT_EXPORT_HEADER_NAME_MODE,
    }
  } catch (error) {
    console.error('Failed to load CSV export configuration:', error)
    return null
  }
}

export const writeStoredExportConfig = (
  schemaName: string,
  { attributeKeys, headerNameMode }: StoredExportConfig,
) => {
  if (typeof localStorage === 'undefined') return false

  try {
    localStorage.setItem(
      getExportConfigStorageKey(schemaName),
      JSON.stringify({
        attributeKeys,
        headerNameMode,
      } satisfies StoredExportConfig),
    )
    return true
  } catch (error) {
    console.error('Failed to save CSV export configuration:', error)
    return false
  }
}

export const normalizeStoredExportAttributeKeys = (
  attributeKeys: string[],
  allSelectableKeys: string[],
) => {
  if (attributeKeys.length === 0) return []

  const selectableKeySet = new Set(allSelectableKeys)
  const normalizedKeys = [
    PINNED_ATTRIBUTE_KEY,
    ...attributeKeys.filter(
      (attributeKey) =>
        attributeKey !== PINNED_ATTRIBUTE_KEY &&
        selectableKeySet.has(attributeKey),
    ),
  ]

  return [...new Set(normalizedKeys)].filter((attributeKey) =>
    selectableKeySet.has(attributeKey),
  )
}
